import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFilters } from '../actions/filters.action';
import { fetchProfile } from '../actions/profile.action';
import { setWalletAddress } from '../actions/wallet.action';
import { CookiesKeys } from '../enums/cookies-keys.enum';
import { IState } from '../models/reducers/i-state.interface';
import Web3Singleton from '../web3Singleton';
import { useCookies } from './useCookies';
import { useIsWeb3Allowed } from './useIsWeb3Allowed';
import { useSignout } from './useSignout';
import { isImpersonatedWalletAddress } from '../utils/dev-impersonation.utils';

export const useAccountChange = (): void => {
  const lgoWeb3 = Web3Singleton.getInstance();
  const web3 = lgoWeb3.getWeb3();
  const isWeb3Allowed = useIsWeb3Allowed();
  const walletAddress = useSelector(({ wallet }: IState) => wallet.address);
  const dispatch = useDispatch();
  const signOut = useSignout();
  const { getCookie } = useCookies();

  const walletAddressRef = useRef(walletAddress);
  const signOutRef = useRef(signOut);
  useEffect(() => {
    walletAddressRef.current = walletAddress;
  }, [walletAddress]);
  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  useEffect(() => {
    if (!isWeb3Allowed) {
      return;
    }

    const handleAccountChange = async (newWalletAddress: string[]): Promise<void> => {
      const currentWalletAddress = walletAddressRef.current;

      if (isImpersonatedWalletAddress(currentWalletAddress)) {
        return;
      }

      if (currentWalletAddress !== newWalletAddress[0]) {
        await signOutRef.current();

        return;
      }

      dispatch(setWalletAddress(newWalletAddress[0]));
    };

    lgoWeb3.onAccountChange(handleAccountChange);

    return () => {
      if (!web3) {
        return;
      }

      lgoWeb3.removeOnAccountChange(handleAccountChange);
    };
  }, [dispatch, isWeb3Allowed, web3, lgoWeb3]);

  useEffect(() => {
    if (!isWeb3Allowed || !walletAddress) {
      return;
    }

    if (isImpersonatedWalletAddress(walletAddress)) {
      return;
    }

    dispatch(fetchFilters());

    const token = getCookie(CookiesKeys.csrftoken);

    if (!token) {
      return;
    }

    dispatch(fetchProfile());
  }, [dispatch, isWeb3Allowed, walletAddress]);
};